import { AxiosResponse } from 'axios';
import axiosClient from '../clients/axios-client';
import {
	ProjectDataCategory,
	ProjectDataCategoryFilter,
	ProjectDataCategoryResponse,
	CreateProjectDataCategoryInput,
	UpdateProjectDataCategoryInput,
	ProjectDataPointDefinition,
	ProjectDataPointDefinitionFilter,
	ProjectDataPointDefinitionResponse,
	CreateProjectDataPointDefinitionInput,
	UpdateProjectDataPointDefinitionInput,
	ProjectDataValue,
	ProjectDataValueFilter,
	ProjectDataValueResponse,
	CreateProjectDataValueInput,
	UpdateProjectDataValueInput,
} from '../data/models/projectData';
import { stringifyReqObj } from '../utils/common-utils';

// --- CATEGORY ---
export async function fetchProjectDataCategories(
	filter: ProjectDataCategoryFilter
): Promise<ProjectDataCategoryResponse> {
	const graphqlQuery = `{
    projectDataCategories(filter: ${stringifyReqObj(filter)}) {
      list {
        id
        created
        updated
        name
        description
        enabled
        company
        projectDataPointDefinitions {
          id
          name
          label
          description
          type
          options { label value }
          isEssential
          displayOrder
          multiple
        }
      }
      count
    }
  }`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.projectDataCategories;
}

export async function createProjectDataCategory(
	data: CreateProjectDataCategoryInput
): Promise<ProjectDataCategory> {
	const graphqlQuery = `mutation { createProjectDataCategory(data: ${stringifyReqObj(
		data
	)}) {
    id
    created
    updated
    name
    description
    associatedProjectTypes
    company
  }}`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.createProjectDataCategory;
}

export async function updateProjectDataCategory(
	data: Partial<UpdateProjectDataCategoryInput>
): Promise<ProjectDataCategory> {
	const graphqlQuery = `mutation { updateProjectDataCategory(data: ${stringifyReqObj(
		data
	)}) {
    id
    created
    updated
    name
    description
    enabled
          company
          associatedProjectConfigurations {
            id
            name
          }
          projectDataPointDefinitions {
            id
            name
            label
            description
            type
            options { label value }
            isEssential
            displayOrder
          }
  }}`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.updateProjectDataCategory;
}

// --- POINT DEFINITION ---
export async function fetchProjectDataPointDefinitions(
	filter: ProjectDataPointDefinitionFilter
): Promise<ProjectDataPointDefinitionResponse> {
	const graphqlQuery = `{
    projectDataPointDefinitions(filter: ${stringifyReqObj(filter)}) {
      list {
        id
        created
        updated
        name
        label
        description
        type
        options { label value }
        isEssential
        displayOrder
        company
        projectDataCategory
        createdBy { id name }
        updatedBy { id name }
      }
      count
    }
  }`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.projectDataPointDefinitions;
}

export async function createProjectDataPointDefinition(
	data: CreateProjectDataPointDefinitionInput
): Promise<ProjectDataPointDefinition> {
	const graphqlQuery = `mutation { createProjectDataPointDefinition(data: ${stringifyReqObj(
		data
	)}) {
    id
    created
    updated
    name
    label
    description
    type
    options { label value }
    isEssential
    displayOrder
    company
    projectDataCategory
    createdBy { id name }
    updatedBy { id name }
  }}`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.createProjectDataPointDefinition;
}

export async function updateProjectDataPointDefinition(
	data: UpdateProjectDataPointDefinitionInput
): Promise<ProjectDataPointDefinition> {
	const graphqlQuery = `mutation { updateProjectDataPointDefinition(data: ${stringifyReqObj(
		data
	)}) {
    id
    created
    updated
    name
    label
    description
    type
    options { label value }
    isEssential
    displayOrder
    company
    projectDataCategory
    createdBy { id name }
    updatedBy { id name }
  }}`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.updateProjectDataPointDefinition;
}

// --- DATA VALUE ---
export async function fetchProjectDataValues(
	filter: ProjectDataValueFilter
): Promise<ProjectDataValueResponse> {
	const graphqlQuery = `{
    projectDataValues(filter: ${stringifyReqObj(filter)}) {
      list {
        id
        created
        updated
        value
        reviewStatus
        syncStatus
        project
        projectDataPointDefinition {
          id
          name
          label
          type
          options { label value }
          isEssential
          displayOrder
          projectDataCategory
          multiple
        }
        projectDataCategoryName
        company
        createdBy { id name }
        updatedBy { id name }
      }
      count
    }
  }`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.projectDataValues;
}

export async function createProjectDataValue(
	data: CreateProjectDataValueInput
): Promise<ProjectDataValue> {
	const graphqlQuery = `mutation { createProjectDataValue(data: ${stringifyReqObj(
		data
	)}) {
    id
    created
    updated
    value
    reviewStatus
    syncStatus
    project
    projectDataPointDefinition
    company
    createdBy { id name }
    updatedBy { id name }
  }}`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.createProjectDataValue;
}

export async function updateProjectDataValue(
	data: UpdateProjectDataValueInput
): Promise<ProjectDataValue> {
	const graphqlQuery = `mutation { updateProjectDataValue(data: {id:"${
		data.id
	}",value:${JSON.stringify(JSON.stringify(data.value))}}) {
    id
    created
    updated
    value
    reviewStatus
    syncStatus
    project
    projectDataCategoryName
    projectDataPointDefinition {
      id
      name
      label
      type
      options { label value }
      isEssential
      displayOrder
      projectDataCategory
      multiple
    }
    company
    createdBy { id name }
    updatedBy { id name }
  }}`;
	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: { query: graphqlQuery },
	});
	return response?.data?.updateProjectDataValue;
}
